/**
 * MODULE: domain/pages/frontend/hooks/usePageTasksSync.ts
 * PURPOSE: TanStack Query hook for page-scoped task list sync via taskApi
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { taskApi, type Task } from '@shared/lib/frontend/api/taskApi';
import {
  buildPageTaskSyncTag,
  filterPageTasks,
  isTaskComplete,
  sortPageTasks,
} from '../utils/pageTasksSyncUtils';

export const PAGE_TASKS_SYNC_KEY = (pageId: string) => ['pages', 'tasks-sync', pageId] as const;

type Options = {
  pageId: string;
  showCompleted: boolean;
  enabled?: boolean;
};

export function usePageTasksSync({ pageId, showCompleted, enabled = true }: Options) {
  const qc = useQueryClient();
  const queryKey = PAGE_TASKS_SYNC_KEY(pageId);

  const query = useQuery({
    queryKey,
    queryFn: () => taskApi.list({ limit: 200 }),
    enabled: Boolean(pageId) && enabled,
    staleTime: 30_000,
  });

  const tasks = sortPageTasks(
    filterPageTasks(query.data ?? [], pageId, showCompleted)
  );

  const invalidate = () => qc.invalidateQueries({ queryKey });

  const createTask = useMutation({
    mutationFn: (title: string) =>
      taskApi.create({
        title: title.trim(),
        status: 'todo',
        priority: 'medium',
        tags: [buildPageTaskSyncTag(pageId)],
        ai_context: `Created from page ${pageId}`,
      }),
    onSuccess: () => invalidate(),
  });

  const toggleTaskDone = useMutation({
    mutationFn: (task: Task) =>
      taskApi.updateStatus(task.id, isTaskComplete(task) ? 'todo' : 'done'),
    onSuccess: () => invalidate(),
  });

  return {
    tasks,
    isLoading: query.isLoading,
    isError: query.isError,
    isFetching: query.isFetching,
    refetch: query.refetch,
    createTask,
    toggleTaskDone,
  };
}
